/**
 * Order Summary Header — V1.3 "Smart Two-Track" Design
 *
 * Layout:
 * 1. Header row: Customer (left) + overall status button (right, clickable to expand)
 * 2. Collapsible: two-track checklist (delivery + billing) with vendor-friendly Thai
 * 3. Metrics grid: 4 columns (order date, delivery date, items, total)
 * 4. Footer: progress bars (delivery + billing) — always visible
 */

import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import type { SectionStructure } from "@/features/inventory/item-detail/utils/buildTabsFromMeta";
import type { DocFieldSchema, DocTypeSchema } from "@/lib/erp/meta";
import { MetaFieldEditModal } from "@/features/inventory/item-detail/components/MetaFieldEditModal";
import { CustomerSelectorModal } from "@/features/selling/sales-order-detail/CustomerSelectorModal";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import {
  Calendar,
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  MessageSquare,
  Package,
  PauseCircle,
  Truck,
  User,
} from "lucide-react";
import { ChannelLogo } from "@/features/channels/components/ChannelLogo";

/* ── Helpers ─────────────────────────────────────────────── */

function formatFieldValue(
  field: any,
  value: any,
  language?: "en" | "th",
  getYesNoLabel?: (value: boolean) => string,
): string {
  if (value === null || value === undefined || value === "") return "-";
  switch (field?.fieldtype) {
    case "Check":
      if (getYesNoLabel) return getYesNoLabel(value === 1 || value === true);
      if (language) {
        const y = getUITranslation("yes", language) || "Yes";
        const n = getUITranslation("no", language) || "No";
        return value === 1 || value === true ? y : n;
      }
      return value === 1 || value === true ? "Yes" : "No";
    default:
      return String(value || "-");
  }
}

function getFieldValue(
  fieldname: string,
  formData: Record<string, any>,
): any {
  return formData[fieldname];
}

function getFieldSchema(
  fieldname: string,
  metaFields: DocFieldSchema[],
): DocFieldSchema | undefined {
  return metaFields.find((f) => f.fieldname === fieldname);
}

/* ── Vendor-friendly labels ──────────────────────────────── */

function deliveryLabelTh(pct: number): string {
  if (pct === 0) return "ยังไม่ได้ส่งของ";
  if (pct >= 100) return "ส่งของครบแล้ว";
  return "ส่งของแล้วบางส่วน";
}

function billingLabelTh(pct: number): string {
  if (pct === 0) return "ยังไม่ได้เก็บเงิน";
  if (pct >= 100) return "เก็บเงินครบแล้ว";
  return "เก็บเงินแล้วบางส่วน";
}

type StatusInfo = {
  text: string;
  color: string;
  dotColor: string;
  pulse: boolean;
  onHold: boolean;
};

function deriveStatusInfo(
  status: string,
  perDelivered: number,
  perBilled: number,
): StatusInfo {
  const s = (status || "").trim();

  if (s === "On Hold") {
    return { text: "พักไว้ก่อน", color: "text-neutral-500 dark:text-neutral-400", dotColor: "bg-neutral-400", pulse: false, onHold: true };
  }
  if (s === "Cancelled") {
    return { text: "ยกเลิกแล้ว", color: "text-red-600 dark:text-red-400", dotColor: "bg-red-500", pulse: false, onHold: false };
  }
  if (s === "Closed") {
    return { text: "ปิดแล้ว", color: "text-neutral-500 dark:text-neutral-400", dotColor: "bg-neutral-400", pulse: false, onHold: false };
  }
  if (s === "Draft") {
    return { text: "แบบร่าง", color: "text-neutral-500 dark:text-neutral-400", dotColor: "bg-neutral-400", pulse: false, onHold: false };
  }

  // Completed
  if (perDelivered >= 100 && perBilled >= 100) {
    return { text: "เสร็จสิ้น", color: "text-green-600 dark:text-green-400", dotColor: "bg-green-500", pulse: false, onHold: false };
  }

  // Delivered but unpaid
  if (perDelivered >= 100 && perBilled < 100) {
    return {
      text: perBilled > 0 ? "ส่งของครบแล้ว · เก็บเงินบางส่วน" : "ส่งของครบแล้ว · รอเก็บเงิน",
      color: "text-amber-600 dark:text-amber-400",
      dotColor: "bg-amber-500",
      pulse: true,
      onHold: false,
    };
  }

  // Paid but not delivered
  if (perBilled >= 100 && perDelivered < 100) {
    return {
      text: perDelivered > 0 ? "รับเงินครบแล้ว · ส่งของบางส่วน" : "รับเงินครบแล้ว · รอส่งของ",
      color: "text-blue-600 dark:text-blue-400",
      dotColor: "bg-blue-500",
      pulse: true,
      onHold: false,
    };
  }

  // Both partial
  if (perDelivered > 0 || perBilled > 0) {
    const parts: string[] = [];
    if (perDelivered > 0) parts.push("ส่งของบางส่วน");
    if (perBilled > 0) parts.push("เก็บเงินบางส่วน");
    return {
      text: parts.join(" · ") || "กำลังดำเนินการ",
      color: "text-blue-600 dark:text-blue-400",
      dotColor: "bg-blue-500",
      pulse: true,
      onHold: false,
    };
  }

  // Fresh order
  return { text: "ออเดอร์ใหม่", color: "text-orange-600 dark:text-orange-400", dotColor: "bg-orange-500", pulse: true, onHold: false };
}

/* ── ChecklistItem ───────────────────────────────────────── */

function ChecklistItem({
  done,
  active,
  label,
}: {
  done: boolean;
  active: boolean;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <div
        className={cn(
          "flex h-5 w-5 items-center justify-center rounded-md shrink-0",
          done && "bg-green-500",
          active && !done && "border-2 border-blue-400 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-500",
          !done && !active && "border-2 border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800",
        )}
      >
        {done && <Check className="h-3 w-3 text-white" />}
      </div>
      <span
        className={cn(
          "text-sm",
          done && "text-neutral-500 line-through dark:text-neutral-400",
          active && !done && "text-neutral-800 font-medium dark:text-neutral-200",
          !done && !active && "text-neutral-400 dark:text-neutral-500",
        )}
      >
        {label}
      </span>
    </div>
  );
}

/* ── Props ───────────────────────────────────────────────── */

interface ShopifyOrderHeaderProps {
  section: SectionStructure;
  formData: Record<string, any>;
  metaFields: DocFieldSchema[];
  meta?: DocTypeSchema;
  customerSection?: SectionStructure | null;
  onFieldChange?: (fieldname: string, value: any) => void;
  onSave?: () => void;
  disabled?: boolean;
  isSaving?: boolean;
  isNew?: boolean;
  documentName?: string;
}

/* ── Component ───────────────────────────────────────────── */

export function ShopifyOrderHeader({
  formData,
  metaFields,
  meta,
  customerSection,
  onFieldChange,
  disabled,
  isSaving,
  isNew,
  documentName,
}: ShopifyOrderHeaderProps) {
  const [stepMapOpen, setStepMapOpen] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerSelectorOpen, setCustomerSelectorOpen] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const { language } = useLanguage();

  /* ── Data extraction ─────────────────────────────────── */

  const dateFields = useMemo(() => {
    const fields = meta?.fields || metaFields;
    return [
      fields.find((f) => f.fieldname === "transaction_date"),
      fields.find((f) => f.fieldname === "delivery_date"),
    ].filter((f): f is DocFieldSchema => !!f);
  }, [meta, metaFields]);

  const customerFieldsFallback = useMemo(() => {
    if (customerSection) return null;
    const fields = meta?.fields || metaFields;
    return [
      fields.find((f) => f.fieldname === "customer"),
      fields.find((f) => f.fieldname === "company"),
    ].filter((f): f is DocFieldSchema => !!f);
  }, [customerSection, meta, metaFields]);

  const uiLabels = useMemo(() => {
    const g = (key: string, fb: string) => getUITranslation(key, language) || fb;
    return {
      orderDate: g("order_date", "Order Date"),
      deliveryDate: g("delivery_date", "Delivery Date"),
      totalItems: g("total_items", "Total Items"),
      totalAmount: g("total_amount", "Total Amount"),
      customer: g("customer", "Customer"),
      yes: g("yes", "Yes"),
      no: g("no", "No"),
      selectOrderDate: g("select_order_date", "Select order date..."),
      selectDeliveryDate: g("select_delivery_date", "Select delivery date..."),
      selectCustomer: g("select_customer", "Select customer..."),
      orderDeliveryDates: g("order_delivery_dates", "Order & Delivery Dates"),
    };
  }, [language]);

  const status = getFieldValue("status", formData) || "";
  const transactionDate = getFieldValue("transaction_date", formData);
  const deliveryDate = getFieldValue("delivery_date", formData);
  const grandTotal = getFieldValue("grand_total", formData);
  const currency = getFieldValue("currency", formData) || "THB";
  const totalQty = getFieldValue("total_qty", formData);
  const items = getFieldValue("items", formData);
  const perDelivered = Number(formData.per_delivered || 0);
  const perBilled = Number(formData.per_billed || 0);

  const dateField = getFieldSchema("transaction_date", metaFields);
  const deliveryDateField = getFieldSchema("delivery_date", metaFields);

  const getYesNoLabel = useMemo(
    () => (value: boolean) => (value ? uiLabels.yes : uiLabels.no),
    [uiLabels.yes, uiLabels.no],
  );

  const formattedDate = dateField
    ? formatFieldValue(dateField, transactionDate, language, getYesNoLabel)
    : transactionDate;
  const formattedDeliveryDate = deliveryDateField
    ? formatFieldValue(deliveryDateField, deliveryDate, language, getYesNoLabel)
    : deliveryDate;

  const calculatedTotalQty = useMemo(() => {
    if (totalQty != null) return Number(totalQty);
    if (Array.isArray(items) && items.length > 0) {
      return items.reduce((sum, item) => sum + Number(item.qty || item.quantity || 0), 0);
    }
    return 0;
  }, [items, totalQty]);

  const formatCurrency = (amount: any, curr: string) => {
    if (!amount && amount !== 0) return "-";
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(num)) return "-";
    const symbols: Record<string, string> = { THB: "฿", USD: "$", EUR: "€", GBP: "£" };
    return `${symbols[curr] || curr}${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const customer = getFieldValue("customer", formData);
  const customerField = getFieldSchema("customer", metaFields);
  const formattedCustomer = customerField
    ? formatFieldValue(customerField, customer, language, getYesNoLabel)
    : customer;

  const canEditCustomer =
    onFieldChange &&
    !disabled &&
    (customerSection || (customerFieldsFallback && customerFieldsFallback.length > 0));
  const canEditDates = !disabled && onFieldChange && dateFields.length > 0;

  const statusInfo = deriveStatusInfo(status, perDelivered, perBilled);

  /* ── Render ──────────────────────────────────────────── */

  return (
    <>
      <div className="rounded-[20px] bg-white border border-neutral-200/50 shadow-[0_2px_8px_rgba(0,0,0,0.04)] dark:bg-neutral-900 dark:border-neutral-800/50 overflow-hidden">
        {/* ── Channel data section (marketplace orders only) ── */}
        {formData.source_channel && !String(formData.source_channel).startsWith("Storefront") && (
          <ChannelDataSection formData={formData} />
        )}
        {/* ── Header: Customer + Status ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 dark:border-neutral-800/50">
          <div
            className={cn(
              "flex items-center gap-3",
              canEditCustomer && "cursor-pointer hover:opacity-80 transition-opacity",
            )}
            onClick={() => canEditCustomer && (isNew ? setCustomerSelectorOpen(true) : setCustomerModalOpen(true))}
            role={canEditCustomer ? "button" : undefined}
            tabIndex={canEditCustomer ? 0 : undefined}
            onKeyDown={(e) => e.key === "Enter" && canEditCustomer && (isNew ? setCustomerSelectorOpen(true) : setCustomerModalOpen(true))}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800">
              <User className="h-4 w-4 text-neutral-500" />
            </div>
            <div>
              <p className="text-base font-medium text-neutral-900 dark:text-neutral-100">
                {formattedCustomer && formattedCustomer !== "-" ? (
                  formattedCustomer
                ) : (
                  <span className="text-neutral-400 italic">{uiLabels.selectCustomer}</span>
                )}
              </p>
              {customer && (
                <p className="text-xs text-neutral-400">{customer}</p>
              )}
            </div>
          </div>

          {/* Status toggle */}
          {!isNew && (
            <button
              onClick={() => setStepMapOpen(!stepMapOpen)}
              className="flex items-center gap-2.5 px-4 py-2 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
            >
              {statusInfo.onHold ? (
                <PauseCircle className="h-4 w-4 text-neutral-400" />
              ) : (
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  {statusInfo.pulse && (
                    <span className={`absolute inline-flex h-full w-full rounded-full ${statusInfo.dotColor} opacity-75 animate-ping`} />
                  )}
                  <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${statusInfo.dotColor}`} />
                </span>
              )}
              <span className={`text-base font-medium ${statusInfo.color}`}>
                {statusInfo.text}
              </span>
              <ChevronDown
                className={`h-4 w-4 text-neutral-400 transition-transform duration-200 ${stepMapOpen ? "rotate-180" : ""}`}
              />
            </button>
          )}
        </div>

        {/* ── Collapsible Two-Track Checklist ── */}
        {!isNew && (
          <div
            className={`grid transition-all duration-300 ease-in-out ${
              stepMapOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="overflow-hidden">
              <div
                className={cn(
                  "px-6 py-5 border-b border-neutral-100 dark:border-neutral-800/50",
                  statusInfo.onHold
                    ? "bg-neutral-100/60 dark:bg-neutral-800/40"
                    : "bg-neutral-50/60 dark:bg-neutral-800/20",
                )}
              >
                {statusInfo.onHold ? (
                  <div className="flex items-center gap-3 py-2">
                    <PauseCircle className="h-5 w-5 text-neutral-400" />
                    <div>
                      <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                        ออเดอร์นี้ถูกพักไว้ก่อน
                      </p>
                      <p className="text-xs text-neutral-400 mt-0.5">
                        กลับมาจัดการต่อได้ตลอดเวลา
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-6">
                    {/* Delivery checklist */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Truck className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          ส่งของ
                        </span>
                        <span
                          className={cn(
                            "text-xs font-semibold ml-auto",
                            perDelivered >= 100 && "text-green-600",
                            perDelivered > 0 && perDelivered < 100 && "text-blue-600",
                            perDelivered === 0 && "text-neutral-400",
                          )}
                        >
                          {deliveryLabelTh(perDelivered)}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <ChecklistItem done={perDelivered > 0} active={perDelivered === 0} label="รับออเดอร์" />
                        <ChecklistItem done={perDelivered >= 50} active={perDelivered > 0 && perDelivered < 100} label="จัดเตรียมสินค้า" />
                        <ChecklistItem done={perDelivered >= 100} active={false} label="ส่งของให้ลูกค้า" />
                      </div>
                    </div>

                    {/* Billing checklist */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <CreditCardIcon className="h-4 w-4 text-emerald-500" />
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          เก็บเงิน
                        </span>
                        <span
                          className={cn(
                            "text-xs font-semibold ml-auto",
                            perBilled >= 100 && "text-green-600",
                            perBilled > 0 && perBilled < 100 && "text-emerald-600",
                            perBilled === 0 && "text-neutral-400",
                          )}
                        >
                          {billingLabelTh(perBilled)}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <ChecklistItem done={perBilled > 0} active={perBilled === 0} label="ออกบิล / แจ้งยอด" />
                        <ChecklistItem done={perBilled >= 50} active={perBilled > 0 && perBilled < 100} label="รับชำระเงิน" />
                        <ChecklistItem done={perBilled >= 100} active={false} label="เก็บเงินครบ" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Metrics Grid: 4 columns ── */}
        <div
          className={cn(
            "grid grid-cols-4 divide-x divide-neutral-200/60 dark:divide-neutral-700/50 bg-neutral-50/80 dark:bg-neutral-800/30",
            canEditDates && "cursor-pointer",
          )}
          onClick={() => canEditDates && setDateModalOpen(true)}
          role={canEditDates ? "button" : undefined}
          tabIndex={canEditDates ? 0 : undefined}
          onKeyDown={(e) => e.key === "Enter" && canEditDates && setDateModalOpen(true)}
        >
          <div className="px-6 py-4">
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-1">
              {uiLabels.orderDate}
            </p>
            <div className="flex items-center gap-2.5">
              <Calendar className="h-4 w-4 text-neutral-400 shrink-0" />
              <p className="text-base text-neutral-800 dark:text-neutral-200">
                {formattedDate && formattedDate !== "-" ? (
                  formattedDate
                ) : (
                  <span className="text-neutral-400 italic">{uiLabels.selectOrderDate}</span>
                )}
              </p>
            </div>
          </div>
          <div className="px-6 py-4">
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-1">
              {uiLabels.deliveryDate}
            </p>
            <div className="flex items-center gap-2.5">
              <Truck className="h-4 w-4 text-neutral-400 shrink-0" />
              <p className="text-base text-neutral-800 dark:text-neutral-200">
                {formattedDeliveryDate && formattedDeliveryDate !== "-" ? (
                  formattedDeliveryDate
                ) : (
                  <span className="text-neutral-400 italic">{uiLabels.selectDeliveryDate}</span>
                )}
              </p>
            </div>
          </div>
          <div
            className="px-6 py-4"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-1">
              {uiLabels.totalItems}
            </p>
            <div className="flex items-center gap-2.5">
              <Package className="h-4 w-4 text-neutral-400 shrink-0" />
              <p className="text-base text-neutral-800 dark:text-neutral-200">
                {calculatedTotalQty > 0 ? `${calculatedTotalQty} รายการ` : "-"}
              </p>
            </div>
          </div>
          <div
            className="px-6 py-4"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-1">
              {uiLabels.totalAmount}
            </p>
            <p className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              {formatCurrency(grandTotal, currency)}
            </p>
          </div>
        </div>

        {/* ── Footer: Progress Bars (always visible) ── */}
        {!isNew && (
          <div className="px-6 py-3.5 border-t border-neutral-100 dark:border-neutral-800/50">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2.5 flex-1">
                <span className="text-xs text-neutral-400 shrink-0">ส่งของ</span>
                <div className="flex-1 h-2 rounded-full bg-neutral-200/60 dark:bg-neutral-700/40 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${perDelivered >= 100 ? "bg-green-500" : "bg-blue-500"}`}
                    style={{ width: `${Math.min(perDelivered, 100)}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-neutral-500 w-10 text-right">
                  {Math.round(perDelivered)}%
                </span>
              </div>
              <div className="w-px h-5 bg-neutral-200/60 dark:bg-neutral-700/40" />
              <div className="flex items-center gap-2.5 flex-1">
                <span className="text-xs text-neutral-400 shrink-0">เก็บเงิน</span>
                <div className="flex-1 h-2 rounded-full bg-neutral-200/60 dark:bg-neutral-700/40 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${perBilled >= 100 ? "bg-green-500" : "bg-emerald-500"}`}
                    style={{ width: `${Math.min(perBilled, 100)}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-neutral-500 w-10 text-right">
                  {Math.round(perBilled)}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {onFieldChange && dateFields.length > 0 && (
        <MetaFieldEditModal
          open={dateModalOpen}
          onOpenChange={setDateModalOpen}
          fields={dateFields}
          title={uiLabels.orderDeliveryDates}
          formData={formData}
          onFieldChange={onFieldChange}
          disabled={disabled}
          isSaving={isSaving}
          documentName={isNew ? undefined : documentName}
        />
      )}
      {onFieldChange &&
        (customerSection || (customerFieldsFallback && customerFieldsFallback.length > 0)) && (
          <MetaFieldEditModal
            open={customerModalOpen}
            onOpenChange={setCustomerModalOpen}
            section={customerSection || undefined}
            fields={customerSection ? undefined : customerFieldsFallback || undefined}
            title={uiLabels.customer}
            formData={formData}
            onFieldChange={onFieldChange}
            disabled={disabled}
            isSaving={isSaving}
            documentName={isNew ? undefined : documentName}
          />
        )}
      {isNew && onFieldChange && (
        <CustomerSelectorModal
          open={customerSelectorOpen}
          onOpenChange={setCustomerSelectorOpen}
          onSelectCustomer={(name) => {
            if (name) {
              // New customer created — set on order
              onFieldChange("customer", name);
            } else {
              // User chose "existing" — open the search modal
              setCustomerModalOpen(true);
            }
          }}
        />
      )}
    </>
  );
}

/* ── Channel Data Section ────────────────────────────────── */

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === "—") return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="ml-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
      title="Copy"
    >
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function ChannelField({ label, value, copyable }: { label: string; value?: string | null; copyable?: boolean }) {
  const display = value || "—";
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-0.5">{label}</p>
      <div className="flex items-center gap-0.5">
        <span className={cn(
          "text-sm font-medium truncate",
          display === "—" ? "text-neutral-300 dark:text-neutral-600 italic" : "text-neutral-800 dark:text-neutral-200",
        )}>
          {display}
        </span>
        {copyable && display !== "—" && <CopyButton value={display} />}
      </div>
    </div>
  );
}

function shopeeStatusLabel(status: string): string {
  const s = (status || "").toUpperCase();
  const map: Record<string, string> = {
    READY_TO_SHIP: "Ready to Ship",
    SHIPPED: "Shipped",
    COMPLETED: "Completed",
    CANCELLED: "Cancelled",
    IN_CANCEL: "Cancelling",
    INVOICE_PENDING: "Invoice Pending",
    UNPAID: "Unpaid",
    RETRY_SHIP: "Retry Ship",
    PROCESSED: "Processed",
  };
  return map[s] || s || "";
}

function formatShopeeDate(dateStr?: string | null) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return dateStr;
  }
}

function ChannelDataSection({ formData }: { formData: Record<string, any> }) {
  const channel = String(formData.source_channel || "");
  const channelLower = channel.toLowerCase();
  const isShopee = channelLower.includes("shopee");
  const isLazada = channelLower.includes("lazada");
  const isTikTok = channelLower.includes("tiktok");
  const erpStatus = formData.status || "";

  // ── Shopee ──────────────────────────────────────────────
  if (isShopee) {
    const orderSn = formData.shopee_order_sn || "";
    const shopeeStatus = formData.shopee_order_status || "";
    const carrier = formData.shopee_shipping_carrier || "";
    const tracking = formData.shopee_tracking_no || "";
    const shippedAt = formData.shopee_shipped_at || "";
    const labelType = formData.shopee_label_doc_type || "";
    const buyerUsername = formData.shopee_buyer_username || "";
    const buyerMessage = formData.shopee_buyer_message || "";

    const sellerCenterUrl = orderSn
      ? `https://seller.shopee.co.th/portal/sale/${orderSn}`
      : "https://seller.shopee.co.th/portal/sale/";

    const marketplaceLabel = shopeeStatusLabel(shopeeStatus);

    return (
      <div className="border-b border-neutral-100 dark:border-neutral-800/50">
        <ChannelHeader channel={channel} erpStatus={erpStatus} statusLabel={marketplaceLabel} />
        <div className="px-6 pb-3 grid grid-cols-2 gap-x-6 gap-y-3">
          <ChannelField label="Order SN" value={orderSn} copyable />
          <ChannelField label="Buyer" value={buyerUsername} />
          <ChannelField label="Carrier" value={carrier} />
          <ChannelField label="Tracking No." value={tracking} copyable />
          <ChannelField label="Shipped Date" value={formatShopeeDate(shippedAt)} />
          <ChannelField label="Label Type" value={labelType} />
        </div>
        {buyerMessage && (
          <div className="px-6 pb-3">
            <div className="flex items-start gap-2.5 rounded-lg border border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800/60 px-3.5 py-3">
              <MessageSquare className="h-4 w-4 text-neutral-500 dark:text-neutral-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Buyer Message</p>
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 break-words leading-relaxed">{buyerMessage}</p>
              </div>
            </div>
          </div>
        )}
        <div className="px-6 pb-3">
          <a
            href={sellerCenterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition-colors"
          >
            Open Shopee Seller Center
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    );
  }

  // ── Lazada ──────────────────────────────────────────────
  if (isLazada) {
    const orderId = formData.lazada_order_id || "";
    const orderNumber = formData.lazada_order_number || "";
    const lazadaStatus = formData.lazada_order_status || "";
    const tradeOrderId = formData.lazada_trade_order_id || "";

    const sellerCenterUrl = orderId
      ? `https://sellercenter.lazada.co.th/apps/order/detail?tradeOrderId=${tradeOrderId || orderId}`
      : "https://sellercenter.lazada.co.th/apps/order/list";

    return (
      <div className="border-b border-neutral-100 dark:border-neutral-800/50">
        <ChannelHeader channel={channel} erpStatus={erpStatus} statusLabel={lazadaStatus} />
        <div className="px-6 pb-3 grid grid-cols-2 gap-x-6 gap-y-3">
          <ChannelField label="Order ID" value={orderId} copyable />
          <ChannelField label="Order Number" value={orderNumber} copyable />
          {tradeOrderId && <ChannelField label="Trade Order ID" value={tradeOrderId} copyable />}
        </div>
        <div className="px-6 pb-3">
          <a
            href={sellerCenterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
          >
            Open Lazada Seller Center
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    );
  }

  // ── TikTok Shop ─────────────────────────────────────────
  if (isTikTok) {
    const orderId = formData.tiktok_order_id || "";
    const orderNumber = formData.tiktok_order_number || "";

    const sellerCenterUrl = "https://seller-th.tiktok.com/order";

    return (
      <div className="border-b border-neutral-100 dark:border-neutral-800/50">
        <ChannelHeader channel={channel} erpStatus={erpStatus} />
        <div className="px-6 pb-3 grid grid-cols-2 gap-x-6 gap-y-3">
          <ChannelField label="Order ID" value={orderId} copyable />
          {orderNumber && <ChannelField label="Order Number" value={orderNumber} copyable />}
        </div>
        <div className="px-6 pb-3">
          <a
            href={sellerCenterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300 transition-colors"
          >
            Open TikTok Seller Center
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    );
  }

  // ── Other marketplace: just show logo + name ────────────
  return (
    <div className="flex items-center gap-3 px-6 py-3 border-b border-neutral-100 dark:border-neutral-800/50">
      <ChannelLogo channel={channel} size={24} />
      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{channel}</span>
    </div>
  );
}

/** Shared channel header — logo + name + status label */
function ChannelHeader({ channel, erpStatus, statusLabel }: { channel: string; erpStatus?: string; statusLabel?: string }) {
  return (
    <div className="flex items-center justify-between px-6 py-4">
      <div className="flex items-center gap-3">
        <ChannelLogo channel={channel} size={40} />
        <div>
          <span className="text-base font-semibold text-neutral-800 dark:text-neutral-200">{channel}</span>
          {erpStatus && (
            <p className="text-xs text-neutral-400 dark:text-neutral-500">{erpStatus}</p>
          )}
        </div>
      </div>
      {statusLabel && (
        <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">{statusLabel}</span>
      )}
    </div>
  );
}

/* ── Inline SVG icon (no lucide equivalent) ──────────────── */

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  );
}
